import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect, useDispatch } from "react-redux";
import { Card, Button, Tag, Tooltip } from "antd";
import SubscribeToServiceApplicationButton from "../subscriptionActions/SubscribeToApplicationButton";
import {
  CloseCircleOutlined,
  ClockCircleOutlined,
  SignatureOutlined,
  MinusCircleOutlined,
  FolderViewOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import {
  getServiceApplicationSubscriptionSettings,
  getServiceApplicationSubscriptionStatusForIdentity,
} from "../../actions/api";
import styles from "./ServiceApplicationsListItem.module.css";
import Meta from "antd/es/card/Meta";
import UnsubscribeFromServiceApplicationButton from "../subscriptionActions/UnsubscribeFromApplicationButton";
import { Link } from "react-router-dom";

const ServiceApplicationsListItem = ({ serviceApplication, identity }) => {
  const dispatch = useDispatch();
  const [
    serviceApplicationSubscriptionStatus,
    setServiceApplicationSubscriptionStatus,
  ] = useState();
  const [
    serviceApplicationSubscriptionSettings,
    setServiceApplicationSubscriptionSettings,
  ] = useState();
  const [isLoading, setIsLoading] = useState(true);

  const fetchStatus = useCallback(async () => {
    setServiceApplicationSubscriptionStatus({});

    if (identity) {
      const tmpStatus = await dispatch(
        getServiceApplicationSubscriptionStatusForIdentity(
          serviceApplication.applicationIdentifier,
          identity.id,
        ),
      );
      if (tmpStatus && tmpStatus.payload && tmpStatus.payload.data)
        setServiceApplicationSubscriptionStatus(tmpStatus.payload.data);

      const tmpSettings = await dispatch(
        getServiceApplicationSubscriptionSettings(
          serviceApplication.applicationIdentifier,
        ),
      );
      if (tmpSettings && tmpSettings.payload && tmpSettings.payload.data)
        setServiceApplicationSubscriptionSettings(tmpSettings.payload.data);

      setIsLoading(false);
    }
  }, [dispatch, identity, serviceApplication]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus, identity, serviceApplication]);

  const handleRefresh = () => {
    // dispatch(getMyRequests());
    fetchStatus();
  };

  // Possible Statuses:
  //   Undefined
  //   Subscribed
  //   Invalid
  //   GracePeriod
  //   NotSubscribed

  const renderSubscriptionTag = (applicationStatus, loading = false) => {
    if (loading) {
      return (
        <Tag icon={<SyncOutlined spin />} color="default">
          loading
        </Tag>
      );
    }
    switch (applicationStatus?.status) {
      case "NotSubscribed":
        return (
          <Tag icon={<MinusCircleOutlined />} color="default">
            Not Subscribed
          </Tag>
        );
      case "Subscribed":
        return (
          <Tag icon={<SignatureOutlined />} color="success">
            Subscribed
          </Tag>
        );
      case "GracePeriod":
        return (
          <Tag icon={<ClockCircleOutlined />} color="warning">
            Grace Period
          </Tag>
        );
      default:
        return (
          <Tag icon={<CloseCircleOutlined />} color="error">
            Not Available ({applicationStatus?.status})
          </Tag>
        );
    }
  };

  const renderSubscriptionActions = (
    application,
    applicationSettings,
    applicationStatus,
  ) => {
    let actions = [];
    if (
      applicationStatus?.status === "Subscribed" &&
      applicationSettings?.selfSubscriptionGroupId
    ) {
      actions = [
        <UnsubscribeFromServiceApplicationButton
          key="zz1"
          groupForSubscription={applicationSettings.selfSubscriptionGroupId}
          warning="Unsubscribing from this resource or application will remove your access to it."
          onChange={handleRefresh}
        />,
        <Link key="zz2" to={serviceApplication.homePage} target="_blank">
          <Button type="text" icon={<FolderViewOutlined />}>
            Open
          </Button>
        </Link>,
      ];
    } else if (
      applicationStatus?.status === "Subscribed" &&
      applicationSettings?.selfSubscriptionUrl
    ) {
      actions = [
        <Link
          key="zz222"
          to={applicationSettings?.selfSubscriptionUrl + "?unsubscribe=true"}
        >
          <Button type="text" icon={<FolderViewOutlined />}>
            Unsubscribe
          </Button>
        </Link>,
        <Link key="zz122" to={serviceApplication.homePage} target="_blank">
          <Button type="text" icon={<FolderViewOutlined />}>
            Open
          </Button>
        </Link>,
      ];
    } else if (
      applicationStatus?.status === "NotSubscribed" &&
      applicationSettings?.selfSubscriptionGroupId &&
      applicationSettings?.subscribedRoleId
    ) {
      actions = [
        <SubscribeToServiceApplicationButton
          key="zz3"
          subscribedRole={applicationSettings.subscribedRoleId}
          application={application}
          groupForSubscription={applicationSettings.selfSubscriptionGroupId}
          onChange={handleRefresh}
        />,
        <Button key="zz4" type="text" disabled icon={<FolderViewOutlined />}>
          Open
        </Button>,
      ];
    } else if (
      applicationStatus?.status === "NotSubscribed" &&
      applicationSettings?.selfSubscriptionUrl &&
      applicationSettings?.subscribedRoleId
    ) {
      actions = [
        <Link key="zz11" to={applicationSettings?.selfSubscriptionUrl}>
          <Button type="text" icon={<FolderViewOutlined />}>
            Subscribe
          </Button>
        </Link>,
        <Button key="zz4" type="text" disabled icon={<FolderViewOutlined />}>
          Open
        </Button>,
      ];
    } else {
      actions = [
        <Tooltip
          key="zz5"
          title="Self Subscription is not available for this Application"
        >
          <Button type="text" disabled icon={<CloseCircleOutlined />}>
            Not Available
          </Button>
        </Tooltip>,
        <Link key="zz6" to={serviceApplication.homePage} target="_blank">
          <Button
            type="text"
            disabled={applicationStatus?.status !== "Subscribed"}
            icon={<FolderViewOutlined />}
          >
            Open
          </Button>
        </Link>,
      ];
    }

    return actions;
  };

  return (
    <Card
      className={styles.Card}
      classNames={{
        header: styles.CardHeader,
        actions: styles.CardActions,
      }}
      title={serviceApplication.displayName}
      actions={renderSubscriptionActions(
        serviceApplication,
        serviceApplicationSubscriptionSettings,
        serviceApplicationSubscriptionStatus,
      )}
      loading={isLoading}
    >
      <Meta
        description={renderSubscriptionTag(
          serviceApplicationSubscriptionStatus,
          isLoading,
        )}
        className={styles.CardTags}
      />
      <div>{serviceApplication.description}</div>
    </Card>
  );
};

ServiceApplicationsListItem.propTypes = {
  serviceApplication: PropTypes.object.isRequired,
  identity: PropTypes.object,
};

export default connect((props) => ({
  identity: props.identity,
}))(ServiceApplicationsListItem);
